import React, { useEffect, useState, useCallback, useRef } from 'react';
import mammoth from 'mammoth';
import './DocumentViewerModal.css';
import { API_BASE_URL } from '../../../../API/api';
import { backdropCloseHandlers } from '../../../../utils/backdropClose';
import PermissionGate from '../../../PermissionGate/PermissionGate';

// ── CSV parser (handles quoted fields, CRLF/LF, escaped quotes) ──────────────
function parseCSV(text) {
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;
  const n = text.length;

  for (let i = 0; i < n; i++) {
    const ch = text[i];
    const next = text[i + 1];

    if (inQuotes) {
      if (ch === '"' && next === '"') { field += '"'; i++; }
      else if (ch === '"') { inQuotes = false; }
      else { field += ch; }
    } else {
      if (ch === '"') {
        inQuotes = true;
      } else if (ch === ',') {
        row.push(field); field = '';
      } else if (ch === '\r' && next === '\n') {
        row.push(field); field = '';
        rows.push(row); row = [];
        i++;
      } else if (ch === '\n') {
        row.push(field); field = '';
        rows.push(row); row = [];
      } else {
        field += ch;
      }
    }
  }

  if (field !== '' || row.length > 0) { row.push(field); rows.push(row); }
  if (rows.length > 0 && rows[rows.length - 1].every(f => f === '')) rows.pop();
  return rows;
}

function buildFileUrl(file) {
  if (file.isGoogleDriveItem && file.googleDriveId) {
    return `${API_BASE_URL}/api/projects/${encodeURIComponent(file.projectId)}/google-drive-files/${encodeURIComponent(file.googleDriveId)}/download`;
  }
  return `${API_BASE_URL}/api/files/${file.id}/download?project_id=${encodeURIComponent(file.projectId)}`;
}

function buildContentUrl(file) {
  const folderParam = file.parentDriveId ? `?folder_drive_id=${encodeURIComponent(file.parentDriveId)}` : '';
  return `${API_BASE_URL}/api/projects/${encodeURIComponent(file.projectId)}/google-drive-files/${encodeURIComponent(file.googleDriveId)}/content${folderParam}`;
}

function getXmlLines(text) {
  return String(text ?? '').replace(/\r\n/g, '\n').split('\n');
}

function getOpeningTagName(trimmedLine) {
  if (
    !trimmedLine ||
    !trimmedLine.startsWith('<') ||
    trimmedLine.startsWith('</') ||
    trimmedLine.startsWith('<?') ||
    trimmedLine.startsWith('<!')
  ) {
    return null;
  }

  const match = trimmedLine.match(/^<([A-Za-z_][\w:.-]*)\b/);
  if (!match) return null;
  if (trimmedLine.endsWith('/>')) return null;
  if (trimmedLine.includes(`</${match[1]}>`)) return null;
  return match[1];
}

function getClosingTagName(trimmedLine) {
  const match = trimmedLine.match(/^<\/([A-Za-z_][\w:.-]*)\s*>/);
  return match ? match[1] : null;
}

function buildXmlFoldRanges(lines) {
  const stack = [];
  const ranges = {};

  lines.forEach((line, index) => {
    const trimmedLine = line.trim();
    const closingTagName = getClosingTagName(trimmedLine);

    if (closingTagName) {
      for (let i = stack.length - 1; i >= 0; i -= 1) {
        if (stack[i].tagName === closingTagName) {
          const start = stack[i].lineIndex;
          stack.splice(i, 1);
          if (index > start) {
            ranges[start] = { end: index, tagName: closingTagName };
          }
          break;
        }
      }
      return;
    }

    const openingTagName = getOpeningTagName(trimmedLine);
    if (openingTagName) {
      stack.push({ tagName: openingTagName, lineIndex: index });
    }
  });

  return ranges;
}

const MIN_COL_WIDTH = 60;

// ── Resizable CSV table ───────────────────────────────────────────────────────
function ResizableTable({ headerRow, dataRows }) {
  const [colWidths, setColWidths] = useState(() =>
    headerRow.map(() => 150)
  );
  const dragging = useRef(null);

  useEffect(() => {
    setColWidths(headerRow.map(() => 150));
  }, [headerRow]);

  const onMouseDown = useCallback((e, colIndex) => {
    e.preventDefault();
    dragging.current = {
      colIndex,
      startX: e.clientX,
      startWidth: colWidths[colIndex],
    };

    const onMouseMove = (e) => {
      if (!dragging.current) return;
      const { colIndex, startX, startWidth } = dragging.current;
      const delta = e.clientX - startX;
      const newWidth = Math.max(MIN_COL_WIDTH, startWidth + delta);
      setColWidths(prev => {
        const next = [...prev];
        next[colIndex] = newWidth;
        return next;
      });
    };

    const onMouseUp = () => {
      dragging.current = null;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [colWidths]);

  return (
    <table className="docviewer-table">
      <colgroup>
        {colWidths.map((w, i) => <col key={i} style={{ width: w }} />)}
      </colgroup>
      {headerRow.length > 0 && (
        <thead>
          <tr>
            {headerRow.map((h, i) => (
              <th key={i} style={{ width: colWidths[i], minWidth: colWidths[i] }}>
                <span className="th-label" title={h}>{h}</span>
                <span
                  className="col-resize-handle"
                  onMouseDown={(e) => onMouseDown(e, i)}
                />
              </th>
            ))}
          </tr>
        </thead>
      )}
      <tbody>
        {dataRows.map((row, ri) => (
          <tr key={ri} className={ri % 2 === 0 ? 'row-even' : 'row-odd'}>
            {row.map((cell, ci) => (
              <td key={ci} style={{ maxWidth: colWidths[ci] }} title={cell}>{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Main modal ────────────────────────────────────────────────────────────────
const DocumentViewerModal = ({ isOpen, onClose, file }) => {
  const [status, setStatus] = useState('loading');
  const [csvRows, setCsvRows] = useState([]);
  const [docxHtml, setDocxHtml] = useState('');
  const [xmlText, setXmlText] = useState('');
  const [xmlEditText, setXmlEditText] = useState('');
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
  const [isDirty, setIsDirty] = useState(false);
  const [fontSize, setFontSize] = useState('md'); // 'sm' | 'md' | 'lg'
  const [docxZoom, setDocxZoom] = useState(1);
  const [collapsedBlocks, setCollapsedBlocks] = useState({});
  const xmlEditorGutterRef = useRef(null);

  const ZOOM_STEP = 0.15;
  const ZOOM_MIN  = 0.4;
  const ZOOM_MAX  = 2.5;

  const ext = file ? file.name.split('.').pop().toLowerCase() : '';

  // Cleanup blob URL on unmount or file change
  useEffect(() => {
    return () => {
      if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
    };
  }, [pdfBlobUrl]);

  useEffect(() => {
    if (!isOpen || !file) return;

    let cancelled = false;
    setStatus('loading');
    setCsvRows([]);
    setDocxHtml('');
    setXmlText('');
    setXmlEditText('');
    setPdfBlobUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null; });
    setErrorMsg('');
    setEditMode(false);
    setSaveStatus('idle');
    setIsDirty(false);
    setFontSize('md');
    setDocxZoom(1);
    setCollapsedBlocks({});

    const url = buildFileUrl(file);

    const load = async () => {
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`Server returned ${res.status}`);

        if (ext === 'csv') {
          const text = await res.text();
          if (!cancelled) { setCsvRows(parseCSV(text)); setStatus('ready'); }
        } else if (ext === 'docx') {
          const buf = await res.arrayBuffer();
          const result = await mammoth.convertToHtml({ arrayBuffer: buf });
          if (!cancelled) { setDocxHtml(result.value); setStatus('ready'); }
        } else if (ext === 'xml') {
          const text = await res.text();
          if (!cancelled) { setXmlText(text); setXmlEditText(text); setStatus('ready'); }
        } else if (ext === 'pdf') {
          const blob = await res.blob();
          const blobUrl = URL.createObjectURL(blob);
          if (!cancelled) { setPdfBlobUrl(blobUrl); setStatus('ready'); }
          else URL.revokeObjectURL(blobUrl);
        }
      } catch (err) {
        if (!cancelled) { setErrorMsg(err.message); setStatus('error'); }
      }
    };

    load();
    return () => { cancelled = true; };
  }, [isOpen, file, ext]);

  const handleClose = useCallback(() => onClose(), [onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleClose]);

  const handleEditToggle = () => {
    if (editMode && isDirty) {
      if (!window.confirm('Discard unsaved changes?')) return;
    }
    setEditMode(prev => !prev);
    setXmlEditText(xmlText);
    setSaveStatus('idle');
    setIsDirty(false);
  };

  const handleXmlChange = (e) => {
    setXmlEditText(e.target.value);
    setIsDirty(e.target.value !== xmlText);
    setSaveStatus('idle');
  };

  const handleToggleXmlBlock = (lineIndex) => {
    setCollapsedBlocks(prev => ({
      ...prev,
      [lineIndex]: !prev[lineIndex],
    }));
  };

  const handleXmlEditorScroll = (e) => {
    if (xmlEditorGutterRef.current) {
      xmlEditorGutterRef.current.scrollTop = e.target.scrollTop;
    }
  };

  const handleSave = async () => {
    if (!file || !file.isGoogleDriveItem) return;
    setSaveStatus('saving');
    try {
      const url = buildContentUrl(file);
      const res = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/xml; charset=utf-8' },
        body: xmlEditText,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Server returned ${res.status}`);
      }
      setXmlText(xmlEditText);
      setIsDirty(false);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (err) {
      setSaveStatus('error');
      setErrorMsg(err.message);
    }
  };

  if (!isOpen || !file) return null;

  const isCSV = ext === 'csv';
  const isXML = ext === 'xml';
  const isPDF = ext === 'pdf';
  const headerRow = isCSV && csvRows.length > 0 ? csvRows[0] : [];
  const dataRows  = isCSV && csvRows.length > 1 ? csvRows.slice(1) : [];
  const xmlLines = isXML ? getXmlLines(editMode ? xmlEditText : xmlText) : [];
  const xmlFoldRanges = isXML && !editMode ? buildXmlFoldRanges(xmlLines) : {};
  const hiddenXmlLines = new Set();
  const visibleXmlRows = [];

  if (isXML && !editMode) {
    Object.entries(collapsedBlocks).forEach(([lineIndex, isCollapsed]) => {
      if (!isCollapsed) return;
      const range = xmlFoldRanges[Number(lineIndex)];
      if (!range) return;
      for (let i = Number(lineIndex) + 1; i <= range.end; i += 1) {
        hiddenXmlLines.add(i);
      }
    });

    xmlLines.forEach((line, index) => {
      if (hiddenXmlLines.has(index)) return;
      const range = xmlFoldRanges[index];
      const isCollapsed = Boolean(collapsedBlocks[index] && range);
      const closingLine = isCollapsed && range ? xmlLines[range.end].trim() : '';
      visibleXmlRows.push({
        index,
        lineNumber: index + 1,
        text: isCollapsed ? `${line} ... ${closingLine}` : line,
        canFold: Boolean(range),
        isCollapsed,
      });
    });
  }

  const badgeClass = { csv: 'badge-csv', docx: 'badge-docx', xml: 'badge-xml', pdf: 'badge-pdf' }[ext] || 'badge-docx';

  return (
    <div className="docviewer-overlay" {...backdropCloseHandlers(handleClose)}>
      <div className="docviewer-content" onClick={(e) => e.stopPropagation()}>

        <div className="docviewer-header">
          <span className={`docviewer-badge ${badgeClass}`}>
            {ext.toUpperCase()}
          </span>
          <h3 className="docviewer-title" title={file.name}>{file.name}</h3>

          {!isCSV && !isXML && !isPDF && status === 'ready' && (
            <div className="docviewer-zoom-actions">
              <button
                className="docviewer-action-btn"
                onClick={() => setDocxZoom(z => Math.max(ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2)))}
                disabled={docxZoom <= ZOOM_MIN}
                title="Zoom out"
              >
                <i className="fa-solid fa-magnifying-glass-minus" />
              </button>
              <button
                className="docviewer-action-btn"
                onClick={() => setDocxZoom(z => Math.min(ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2)))}
                disabled={docxZoom >= ZOOM_MAX}
                title="Zoom in"
              >
                <i className="fa-solid fa-magnifying-glass-plus" />
              </button>
            </div>
          )}

          {isXML && status === 'ready' && (
            <div className="docviewer-xml-actions">
              <div className="xml-fontsize-picker">
                {[
                  { key: 'sm', label: 'S' },
                  { key: 'md', label: 'M' },
                  { key: 'lg', label: 'L' },
                ].map(({ key, label }) => (
                  <button
                    key={key}
                    className={`xml-fontsize-btn xml-fontsize-${key}${fontSize === key ? ' active' : ''}`}
                    onClick={() => setFontSize(key)}
                    title={key === 'sm' ? 'Small text' : key === 'md' ? 'Medium text' : 'Large text'}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="xml-actions-divider" />
              {saveStatus === 'saving' && (
                <span className="xml-save-status saving">Saving…</span>
              )}
              {saveStatus === 'saved' && (
                <span className="xml-save-status saved"><i className="fas fa-check" /> Saved</span>
              )}
              {saveStatus === 'error' && (
                <span className="xml-save-status save-error"><i className="fas fa-exclamation-triangle" /> Save failed</span>
              )}
              <PermissionGate group="developers" fallback={null}>
                {editMode && file.isGoogleDriveItem && (
                  <button
                    className="docviewer-action-btn save-btn"
                    onClick={handleSave}
                    disabled={!isDirty || saveStatus === 'saving'}
                  >
                    <i className="fas fa-save" /> Save to Drive
                  </button>
                )}
                <button
                  className={`docviewer-action-btn${editMode ? ' edit-active' : ''}`}
                  onClick={handleEditToggle}
                >
                  {editMode
                    ? <><i className="fas fa-eye" /> View</>
                    : <><i className="fas fa-pen" /> Edit</>
                  }
                </button>
              </PermissionGate>
            </div>
          )}

          <button className="docviewer-close" onClick={handleClose} aria-label="Close">
            <i className="fas fa-times" />
          </button>
        </div>

        <div className="docviewer-body">
          {status === 'loading' && (
            <div className="docviewer-loading">
              <div className="docviewer-spinner" />
              <p>Loading {isCSV ? 'spreadsheet' : isXML ? 'XML file' : isPDF ? 'PDF' : 'document'}…</p>
            </div>
          )}

          {status === 'error' && (
            <div className="docviewer-error">
              <i className="fas fa-exclamation-triangle" />
              <p>Failed to load file</p>
              <span className="docviewer-error-detail">{errorMsg}</span>
            </div>
          )}

          {status === 'ready' && isCSV && (
            <div className="docviewer-table-wrap">
              {csvRows.length === 0 ? (
                <p className="docviewer-empty">File is empty.</p>
              ) : (
                <ResizableTable headerRow={headerRow} dataRows={dataRows} />
              )}
            </div>
          )}

          {status === 'ready' && !isCSV && !isXML && !isPDF && (
            <div className="docviewer-docx-body">
              <div
                className="docviewer-docx-page"
                style={{ transform: `scale(${docxZoom})`, transformOrigin: 'top center' }}
                dangerouslySetInnerHTML={{ __html: docxHtml }}
              />
            </div>
          )}

          {status === 'ready' && isXML && (
            <div className="docviewer-xml-wrap">
              {editMode ? (
                <div className={`docviewer-xml-editor-shell xml-fs-${fontSize}`}>
                  <div
                    ref={xmlEditorGutterRef}
                    className="docviewer-xml-gutter docviewer-xml-gutter-edit"
                    aria-hidden="true"
                  >
                    {xmlLines.map((_, index) => (
                      <div key={index} className="docviewer-xml-gutter-line">
                        {index + 1}
                      </div>
                    ))}
                  </div>
                  <textarea
                    className={`docviewer-xml-editor xml-fs-${fontSize}`}
                    value={xmlEditText}
                    onChange={handleXmlChange}
                    onScroll={handleXmlEditorScroll}
                    spellCheck={false}
                  />
                </div>
              ) : (
                <div className={`docviewer-xml-view xml-fs-${fontSize}`}>
                  {visibleXmlRows.map(({ index, lineNumber, text, canFold, isCollapsed }) => (
                    <div key={index} className="docviewer-xml-line">
                      <div className="docviewer-xml-line-number">{lineNumber}</div>
                      <button
                        type="button"
                        className={`docviewer-xml-fold-toggle${canFold ? '' : ' is-spacer'}`}
                        onClick={canFold ? () => handleToggleXmlBlock(index) : undefined}
                        aria-label={canFold ? (isCollapsed ? 'Expand XML block' : 'Collapse XML block') : undefined}
                        disabled={!canFold}
                      >
                        {canFold ? (isCollapsed ? '+' : '-') : ''}
                      </button>
                      <code className={`docviewer-xml-line-code${isCollapsed ? ' is-collapsed' : ''}`}>{text}</code>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {status === 'ready' && isPDF && pdfBlobUrl && (
            <iframe
              className="docviewer-pdf-frame"
              src={pdfBlobUrl}
              title={file.name}
            />
          )}
        </div>

      </div>
    </div>
  );
};

export default DocumentViewerModal;
